import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { resetView, zoomToFit } from './actions';
import { PaneContext, useBoard, useBoardMeta, useYMap } from './context';
import { SharePanel } from './SharePanel';
import { LOOKS, sanitizeCss, type BoardMetaSettings } from './BoardSettings';
import { ExportPanel } from './export/ExportPanel';
import { ShortcutsPanel } from './ShortcutsPanel';
import { AboutPanel } from './AboutPanel';
import { StoragePanel } from './StoragePanel';
import { BoardPickerPanel } from './BoardPickerPanel';
import { registerPane } from './paneRegistry';
import { Inspector } from './Inspector';
import { createPaneStore } from './paneStore';
import { StatusBar } from './StatusBar';
import { Toolbar } from './Toolbar';
import { setActivePane, useKeyboard } from './useKeyboard';
import { Viewport } from './viewport/Viewport';
import { useFingerPinch, useFingerTaps } from './viewport/useFingerTaps';
import { applyView, ViewBar } from './views/ViewBar';
import { BoardSettings } from './BoardSettings';
import { PanelContext, type PanelKind } from './panels';
import { LayersPanel } from './layers/LayersPanel';
import { filterSelectable } from './ink';
import { FLASH_EVENT } from './flash';
import { useIsMobile } from '../ui/useMediaQuery';
import { SheetPanel } from './paint/SheetPanel';
import { canRedoAction, canUndoAction, redoAction, undoAction, usePaintSession } from './paint/session';
import { CutoutDrawer } from './drawer/CutoutDrawer';
import { FrameIndex } from './views/FrameIndex';

/** The board CSS editor and its guide: CodeMirror is only fetched when someone opens it. */
const CssEditor = lazy(() => import('./css/CssEditor'));

const DRAWER_OPEN_KEY = 'vds:drawer-open';
const FRAMES_OPEN_KEY = 'vds:frames-open';

/**
 * One view onto a board: viewport, toolbar, inspector, its own camera and selection.
 * Split view (Phase 6) renders two of these against the same BoardDoc.
 */
export function BoardPane({ initialView = null }: { initialView?: string | null }) {
  const board = useBoard();
  const [pane] = useState(() => createPaneStore());
  const [panel, setPanel] = useState<PanelKind>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => (rootRef.current ? registerPane(rootRef.current, { board, pane }) : undefined), [board, pane]);
  useEffect(() => {
    const open = () => setPanel((p) => (p === 'shortcuts' ? null : 'shortcuts'));
    window.addEventListener('vds:shortcuts', open);
    return () => window.removeEventListener('vds:shortcuts', open);
  }, []);
  const meta = useBoardMeta();
  const readOnly = meta.role === 'viewer';
  useKeyboard(board, pane, readOnly);
  // sketch sheets: the layer being painted is a live canvas while paint mode lasts
  usePaintSession(board, pane, !readOnly);
  // paint mode brings the sheet's layer stack up (not on phones, where it would cover the
  // brush strip: the strip's Layers button opens it there), and takes it away again
  const mobile = useIsMobile();
  const mobileAtMount = useRef(mobile);
  // on phones the paint strip takes the inspector's place while painting
  const [painting, setPainting] = useState(false);
  useEffect(() => pane.subscribe((s) => setPainting(s.paintSheetId !== null)), [pane]);
  useEffect(
    () =>
      pane.subscribe((s, prev) => {
        if (s.paintSheetId === prev.paintSheetId) return;
        if (s.paintSheetId && !prev.paintSheetId && !mobile) setPanel('sheet');
        else if (!s.paintSheetId) setPanel((p) => (p === 'sheet' ? null : p));
      }),
    [pane, mobile],
  );

  // Touch: two fingers tapped together undo, three redo. A short caption confirms what happened.
  const [flash, setFlash] = useState<{ id: number; text: string } | null>(null);
  const onFingerTap = useCallback(
    (fingers: number) => {
      if (readOnly) return;
      let text: string;
      const ctx = { board, pane };
      if (fingers === 2) {
        const can = canUndoAction(ctx);
        if (can) void undoAction(ctx);
        text = can ? 'Undo' : 'Nothing to undo';
      } else {
        const can = canRedoAction(ctx);
        if (can) void redoAction(ctx);
        text = can ? 'Redo' : 'Nothing to redo';
      }
      setFlash({ id: Date.now(), text });
    },
    [board, pane, readOnly],
  );
  useFingerTaps(rootRef, onFingerTap);
  // Touch: a very fast two-finger pinch turns the canvas upright again, at 100%.
  const onFingerPinch = useCallback(() => {
    resetView(pane);
    setFlash({ id: Date.now(), text: 'View reset' });
  }, [pane]);
  useFingerPinch(rootRef, onFingerPinch);
  // tools inside the pane raise captions too (the cutout tool reports what it did)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const on = (e: Event) => setFlash({ id: Date.now(), text: String((e as CustomEvent<string>).detail ?? '') });
    el.addEventListener(FLASH_EVENT, on);
    return () => el.removeEventListener(FLASH_EVENT, on);
  }, []);
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), Math.max(900, 500 + flash.text.length * 35));
    return () => clearTimeout(t);
  }, [flash]);
  // the interface can be hidden (Tab, or the eye in the view bar): only the canvas stays
  const [uiHidden, setUiHidden] = useState(false);
  useEffect(
    () =>
      pane.subscribe((s, prev) => {
        if (s.uiHidden === prev.uiHidden) return;
        setUiHidden(s.uiHidden);
        if (s.uiHidden) setFlash({ id: Date.now(), text: 'Interface hidden · Tab shows it' });
      }),
    [pane],
  );
  // The cutout drawer sits beside the canvas (under it on phones). Whether it was left open is
  // remembered per device; in a split view only the first pane brings it back.
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    try {
      if (!mobileAtMount.current && localStorage.getItem(DRAWER_OPEN_KEY) === '1' && !document.querySelector('.drawer')) pane.getState().setDrawerOpen(true);
    } catch {
      /* private mode */
    }
    setDrawerOpen(pane.getState().drawerOpen);
    return pane.subscribe((s, prev) => {
      if (s.drawerOpen === prev.drawerOpen) return;
      setDrawerOpen(s.drawerOpen);
      try {
        localStorage.setItem(DRAWER_OPEN_KEY, s.drawerOpen ? '1' : '0');
      } catch {
        /* private mode */
      }
    });
  }, [pane]);
  // The frame index docks the same way and is remembered the same way.
  const [framesOpen, setFramesOpen] = useState(false);
  useEffect(() => {
    try {
      if (!mobileAtMount.current && localStorage.getItem(FRAMES_OPEN_KEY) === '1' && !document.querySelector('.frame-index')) pane.getState().setFramesOpen(true);
    } catch {
      /* private mode */
    }
    setFramesOpen(pane.getState().framesOpen);
    return pane.subscribe((s, prev) => {
      if (s.framesOpen === prev.framesOpen) return;
      setFramesOpen(s.framesOpen);
      try {
        localStorage.setItem(FRAMES_OPEN_KEY, s.framesOpen ? '1' : '0');
      } catch {
        /* private mode */
      }
    });
  }, [pane]);
  const look = useYMap<unknown>(board.meta) as BoardMetaSettings;
  const lookName = look.look && LOOKS.some((l) => l.id === look.look) ? look.look : undefined;
  // while the CSS editor has unsaved changes, this pane previews them
  const cssDraft = useStore(pane, (s) => s.cssDraft);
  const cssEditorOpen = useStore(pane, (s) => s.cssEditorOpen) && !readOnly;
  const cssSource = cssDraft ?? look.customCss;
  const customCss = cssSource ? sanitizeCss(cssSource) : '';

  // Dev-only handle for scripted testing from the console (never in production builds).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as { __vos?: unknown };
    w.__vos = { board, pane };
    return () => {
      if (w.__vos && (w.__vos as { pane: unknown }).pane === pane) delete w.__vos;
    };
  }, [board, pane]);

  // Frame the content once the viewport knows its size: the requested view, else fit everything.
  useEffect(() => {
    const ctx = { board, pane };
    const frame = () => {
      if (!(initialView && applyView(board, pane, initialView))) zoomToFit(ctx);
    };
    if (pane.getState().size.w) {
      frame();
      return;
    }
    const unsub = pane.subscribe((s, prev) => {
      if (!prev.size.w && s.size.w) {
        frame();
        unsub();
      }
    });
    return unsub;
  }, [board, pane, initialView]);

  // Ink belongs to the draw tool, everything else to the other tools: crossing over drops the
  // part of the selection the new tool cannot act on.
  useEffect(
    () =>
      pane.subscribe((s, prev) => {
        if (s.tool === prev.tool || (s.tool === 'draw') === (prev.tool === 'draw') || !s.selection.size) return;
        const keep = filterSelectable(board, s.selection, s.tool);
        if (keep.length !== s.selection.size) s.select(keep);
      }),
    [board, pane],
  );

  // Strokes from before ink layers existed get their layer once the board has loaded (and, for
  // server boards, merged with the server), so the select tool ignores them from the start.
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    let unsub: (() => void) | undefined;
    void board.whenReady.then(() => {
      if (cancelled) return;
      const provider = board.provider;
      if (!provider) return board.adoptStrokes();
      const run = () => {
        if (provider.store.getState().status !== 'synced') return;
        unsub?.();
        unsub = undefined;
        board.adoptStrokes();
      };
      unsub = provider.store.subscribe(run);
      run();
    });
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [board, readOnly]);

  // Drop selection/editing references to items that no longer exist (undo, remote delete).
  useEffect(
    () =>
      board.index.subscribe(() => {
        const s = pane.getState();
        if (s.editingId && !board.index.has(s.editingId)) s.setEditing(null);
        if (!s.selection.size) return;
        const alive = [...s.selection].filter((id) => board.index.has(id));
        if (alive.length !== s.selection.size) s.select(alive);
      }),
    [board, pane],
  );

  return (
    <PaneContext.Provider value={pane}>
      <PanelContext.Provider value={{ panel, setPanel }}>
        <div
          ref={rootRef}
          className={`pane${uiHidden ? ' is-ui-hidden' : ''}`}
          data-look={lookName}
          data-board={board.boardId}
          style={look.accent ? ({ '--accent': look.accent } as React.CSSProperties) : undefined}
          onPointerDownCapture={() => setActivePane(pane)}
        >
          {customCss && <style>{`@scope (.pane[data-board="${board.boardId}"]) {\n${customCss}\n}`}</style>}
          <div className="pane-main">
            <Viewport />
            <Toolbar />
            <ViewBar />
            {panel === 'settings' ? (
              <BoardSettings onClose={() => setPanel(null)} />
            ) : panel === 'layers' ? (
              <LayersPanel onClose={() => setPanel(null)} />
            ) : panel === 'share' ? (
              <SharePanel onClose={() => setPanel(null)} />
            ) : panel === 'export' ? (
              <ExportPanel onClose={() => setPanel(null)} />
            ) : panel === 'shortcuts' ? (
              <ShortcutsPanel onClose={() => setPanel(null)} />
            ) : panel === 'about' ? (
              <AboutPanel onClose={() => setPanel(null)} />
            ) : panel === 'storage' ? (
              <StoragePanel onClose={() => setPanel(null)} />
            ) : panel === 'boards' && !readOnly ? (
              <BoardPickerPanel onClose={() => setPanel(null)} />
            ) : panel === 'sheet' && !readOnly ? (
              <SheetPanel onClose={() => setPanel(null)} />
            ) : readOnly || (mobile && painting) ? null : (
              <Inspector />
            )}
            {cssEditorOpen && (
              <Suspense fallback={null}>
                <CssEditor onClose={() => pane.getState().setCssEditorOpen(false)} />
              </Suspense>
            )}
            <StatusBar />
            {flash && (
              <div key={flash.id} className="gesture-flash" role="status">
                {flash.text}
              </div>
            )}
          </div>
          {framesOpen && <FrameIndex onClose={() => pane.getState().setFramesOpen(false)} />}
          {drawerOpen && <CutoutDrawer onClose={() => pane.getState().setDrawerOpen(false)} />}
        </div>
      </PanelContext.Provider>
    </PaneContext.Provider>
  );
}
